import { useContext, useRef } from 'react'
import { css, cx, useTheme } from '@fable/theme'
import { FlexBox, Logo, Text } from '@fable/components'
import { Book, Club } from '@fable/types'
import SearchbarResultBook from './SearchbarResultBook'
import SearchbarResultClub from './SearchbarResultClub'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import SearchbarFilters from './SearchbarFilters'
import { SearchContext } from 'state'
import { useBranchView } from 'hooks'
import SearchResultsFooterBook from './SearchResultsFooterBooks'

const SearchResults = ({ className }: { className?: string }) => {
  const {
    hasNextPage,
    allowFilters,
    searchQuery,
    getNextPage,
    resultsFlattened,
    activeFilter,
    input,
  } = useContext(SearchContext)
  const hasResults =
    (!!input && !!resultsFlattened?.length) ||
    (!input && !resultsFlattened?.length)
  const { colors, mediaQueries } = useTheme()
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const bannerInView = useBranchView()

  const handleResultsBottomreached = () => {
    if (
      !searchQuery?.isLoading &&
      !searchQuery?.isFetchingNextPage &&
      hasNextPage &&
      !!resultsFlattened?.length
    ) {
      getNextPage()
    }
  }

  const renderResult = (result: Book | Club) => {
    const type = result.type || activeFilter

    if (type === 'book') {
      return <SearchbarResultBook book={result as Book} />
    }
    if (type === 'club') {
      return <SearchbarResultClub club={result as Club} />
    }

    return null
  }

  return (
    <FlexBox
      flexDirection="column"
      className={cx(
        css`
          width: 100%;
          height: calc(100% - 212px);
          margin: 0 auto;
          background: white;
          ${mediaQueries.mobileL} {
            height: calc(100% - 184px);
          }
        `,
        bannerInView &&
          css`
            height: calc(100% - 256px);
          `,
        className
      )}
    >
      {searchQuery?.isLoading && !searchQuery?.isFetchingNextPage ? (
        <Logo
          animate
          isIcon
          className={css`
            margin: 0 auto;
          `}
        />
      ) : null}
      {allowFilters && <SearchbarFilters />}
      {!hasResults && searchQuery?.isSuccess && input?.length ? (
        <>
          <FlexBox
            className={css`
              height: 48px;
              margin: 20px auto;
            `}
            centerAll
          >
            <Text type="header" sizing="L" text="No results" />
          </FlexBox>
          {activeFilter === 'book' && <SearchResultsFooterBook />}
        </>
      ) : (
        <Virtuoso
          className={css`
            flex: 1 1 auto;
            z-index: 2;
            * {
              cursor: pointer;
            }
          `}
          ref={virtuosoRef}
          endReached={handleResultsBottomreached}
          overscan={200}
          data={(resultsFlattened || []) as Book[]}
          components={{
            Footer: () => (
              <div
                className={css`
                  padding: 20px 0px;
                `}
              >
                {hasNextPage && searchQuery?.isFetchingNextPage ? (
                  <FlexBox centerAll>
                    <Logo
                      isIcon
                      animate
                      className={css`
                        margin: 0 auto;
                      `}
                    />
                  </FlexBox>
                ) : resultsFlattened?.length ? (
                  <SearchResultsFooterBook />
                ) : null}
              </div>
            ),
          }}
          itemContent={(i, result) => (
            <div
              className={css`
                border-bottom: 1px solid
                  ${i === (resultsFlattened || []).length - 1
                    ? 'transparent'
                    : colors.whiteSmoke};
                width: 100%;
                padding: 21px 28px;
                max-width: 856px;
                @media only screen and (min-width: 900px) {
                  margin: 0 auto;
                }
              `}
            >
              <a href={result.url} target="_blank" rel="noreferrer">
                {renderResult(result)}
              </a>
            </div>
          )}
        />
      )}
    </FlexBox>
  )
}

export default SearchResults
